/**
 * BatchConversionProgress Component
 *
 * Shows conversion progress for a batch of files, with per-file status.
 */

import { memo } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock, FileAudio, FileVideo } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import type { BatchFile } from './types';

interface BatchConversionProgressProps {
  files: BatchFile[];
  currentIndex: number;
  outputFormat: string;
}

export const BatchConversionProgress = memo(function BatchConversionProgress({
  files,
  currentIndex,
  outputFormat,
}: BatchConversionProgressProps) {
  const { t } = useTranslation();

  const completedCount = files.filter((f) => f.status === 'complete').length;
  const totalCount = files.length;
  const overallProgress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="card-themed-lg">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-4">
          <RefreshCw className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" />
        </div>
        <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
          {t('converter.batch.converting', {
            defaultValue: 'Converting files...',
          })}
        </h3>
        <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
          {t('converter.batch.progressCount', {
            defaultValue: '{{completed}} of {{total}} files completed',
            completed: completedCount,
            total: totalCount,
          })}
        </p>
      </div>

      {/* Overall progress bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm text-secondary-600 dark:text-secondary-400 mb-2">
          <span>{t('converter.batch.overallProgress', { defaultValue: 'Overall progress' })}</span>
          <span className="font-medium">{overallProgress}%</span>
        </div>
        <div className="w-full bg-secondary-200 dark:bg-secondary-600 rounded-full h-3 overflow-hidden">
          <div
            className="bg-gradient-to-r from-primary-500 to-primary-600 h-full transition-all duration-500 ease-out rounded-full"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
      </div>

      {/* Per-file list */}
      <div className="space-y-3">
        {files.map((batchFile, index) => {
          const Icon = batchFile.mediaType === 'video' ? FileVideo : FileAudio;
          const outputFileName = batchFile.file.name.replace(/\.[^.]+$/, outputFormat);

          return (
            <div
              key={batchFile.id}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                index === currentIndex
                  ? 'border-primary-300 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/10'
                  : 'border-secondary-200 dark:border-secondary-700'
              }`}
            >
              {/* Status icon */}
              <div className="flex-shrink-0">
                {batchFile.status === 'complete' && (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
                {batchFile.status === 'error' && <XCircle className="w-5 h-5 text-red-500" />}
                {batchFile.status === 'converting' && (
                  <RefreshCw className="w-5 h-5 text-primary-500 animate-spin" />
                )}
                {batchFile.status === 'pending' && <Clock className="w-5 h-5 text-secondary-400" />}
              </div>

              {/* File icon */}
              <Icon
                className={`w-5 h-5 flex-shrink-0 ${
                  batchFile.mediaType === 'video' ? 'text-blue-500' : 'text-purple-500'
                }`}
              />

              {/* File info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                  {batchFile.file.name}
                </p>
                {batchFile.status === 'converting' && (
                  <div className="mt-1">
                    <div className="flex items-center justify-between text-xs text-secondary-500 dark:text-secondary-400 mb-1">
                      <span>{batchFile.statusMessage}</span>
                      <span>{Math.round(batchFile.progress)}%</span>
                    </div>
                    <div className="w-full bg-secondary-200 dark:bg-secondary-600 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-primary-500 h-full transition-all duration-300 rounded-full"
                        style={{ width: `${batchFile.progress}%` }}
                      />
                    </div>
                  </div>
                )}
                {batchFile.status === 'complete' && (
                  <p className="text-xs text-green-600 dark:text-green-400 truncate">
                    {outputFileName}
                  </p>
                )}
                {batchFile.status === 'error' && (
                  <p className="text-xs text-red-600 dark:text-red-400 truncate">
                    {batchFile.error}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default BatchConversionProgress;
